import { useState } from "react";
import { motion } from "framer-motion";
import {
  Shield, Zap, Users, ArrowRight, CheckCircle, Store, Briefcase,
  ChevronRight, Menu, X, Building2, BarChart3, Smartphone,
  Tag, Star, Heart, RefreshCcw, Scissors,
  TrendingUp, CreditCard, Wallet, MapPin, Globe, QrCode,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useBranding } from "@/contexts/BrandingContext";
import { useAuth, getRoleRedirect } from "@/contexts/AuthContext";
import { getPortalForRole, getPortalUrl } from "@/lib/portal";
import { getActivePortal, getIndicatorAccountUrl, setSignedOutIndicator } from "@/lib/session-indicator";
import { supabase } from "@/integrations/supabase/client";

const reveal = { hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: "easeOut" as const } } };

const PORTALS = [
  { key: "corporate", icon: Building2, title: "Corporate Portal", subtitle: "For businesses",
    desc: "Fund your float, issue vouchers to employees or partners, and monitor every transaction with full audit trails.",
    features: ["Bulk issuance & scheduling", "Float management & top-ups", "Full audit trail & reporting"],
    accent: "border-l-blue-500", dot: "bg-blue-500",
    registerHref: "https://corporate.valuegiftvouchers.com/register", cta: "Start Issuing" },
  { key: "merchant", icon: Store, title: "Merchant Portal", subtitle: "For retailers",
    desc: "Accept voucher payments, receive instant MoMo or bank settlement, and track every redemption in one dashboard.",
    features: ["QR & USSD redemption", "Instant MoMo / bank settlement", "Live redemption analytics"],
    accent: "border-l-emerald-500", dot: "bg-emerald-500",
    registerHref: "https://merchant.valuegiftvouchers.com/register", cta: "Join Network" },
  { key: "agent", icon: Users, title: "Agent Portal", subtitle: "For resellers",
    desc: "Sell vouchers across your territory, manage your client accounts, and earn commission on every sale.",
    features: ["Territory & client management", "Commission tracking & payouts", "Offline voucher activation"],
    accent: "border-l-violet-500", dot: "bg-violet-500",
    registerHref: "https://agent.valuegiftvouchers.com/register", cta: "Become an Agent" },
  { key: "consumer", icon: Wallet, title: "Consumer Wallet", subtitle: "For individuals",
    desc: "Store, manage and redeem your digital vouchers at thousands of merchants across Ghana.",
    features: ["QR scan & USSD *920# dial", "Multi-merchant acceptance", "Balance & spend history"],
    accent: "border-l-amber-500", dot: "bg-amber-500",
    registerHref: "https://app.valuegiftvouchers.com/register", cta: "Get Your Wallet" },
];

const VOUCHER_TYPES = [
  { icon: CreditCard, label: "Open Loop",        desc: "Spend anywhere in the network" },
  { icon: Zap,        label: "Fuel Vouchers",     desc: "Pump-specific fuel credits" },
  { icon: Tag,        label: "Category Specific", desc: "Grocery, pharmacy, restaurant…" },
  { icon: Store,      label: "Merchant Specific", desc: "Locked to a single merchant" },
  { icon: Star,       label: "Campaign",          desc: "Promotions & loyalty rewards" },
  { icon: Heart,      label: "Aid & Welfare",     desc: "Government & NGO distribution" },
  { icon: RefreshCcw, label: "Subscription",      desc: "Recurring credit plans" },
  { icon: Scissors,   label: "Split Vouchers",    desc: "One voucher, multiple spends" },
];

const FEATURES = [
  { icon: Shield,     title: "Secure & BoG Compliant",
    desc: "Full KYC via NIA Ghana Card, fraud detection, immutable audit trails, and Bank of Ghana compliance built in." },
  { icon: Zap,        title: "Real-Time Settlement",
    desc: "Merchants receive instant payouts via MTN MoMo, Telecel Cash, AirtelTigo Money, or GhIPSS bank transfer." },
  { icon: Globe,      title: "Multi-Merchant Network",
    desc: "One voucher accepted at thousands of merchant locations. Category-specific or fully open — you decide." },
  { icon: Smartphone, title: "Multi-Channel Delivery",
    desc: "Send via SMS, WhatsApp, email, or the VGV app. Recipients redeem by QR scan or USSD dial." },
  { icon: BarChart3,  title: "Real-Time Analytics",
    desc: "Live dashboards for issuers, merchants, and agents — spend tracking, redemption rates, float management." },
  { icon: MapPin,     title: "Agent Distribution",
    desc: "A nationwide network of licensed agents sells and activates vouchers, extending reach beyond digital channels." },
];

const STEPS = [
  { step: "01", icon: Briefcase, title: "Issue",
    desc: "Corporates or NGOs fund their wallet and configure vouchers — set type, amount, category restrictions, and expiry." },
  { step: "02", icon: Smartphone, title: "Distribute",
    desc: "Recipients receive vouchers via SMS, WhatsApp, or email with a unique QR code and USSD fallback." },
  { step: "03", icon: Store, title: "Redeem",
    desc: "Consumers redeem at any participating merchant — scan QR, tap in-app, or dial USSD. Settlement is instant." },
];

// ── Component ────────────────────────────────────────────────────────────────

export default function LandingPage() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { fullLogo, branding } = useBranding();
  const { user, profile } = useAuth();

  // Real session on this domain (main site localStorage), or indicator cookie
  // set during login on any portal subdomain.
  const indicatorPortal = getActivePortal();

  const portalUrl = (() => {
    if (user && profile) {
      const portalId = getPortalForRole(profile.role);
      if (portalId) return getPortalUrl(portalId, getRoleRedirect(profile.role));
    }
    if (indicatorPortal) return getIndicatorAccountUrl(indicatorPortal);
    return null;
  })();

  const isLoggedIn = !!user || !!indicatorPortal;
  const accountHref = portalUrl ?? "/";

  // Clear the cross-portal indicator and sign out the local main-domain
  // session. Other portal tabs (if any) will self-evict the moment they
  // regain focus — ProtectedRoute polls the indicator and re-renders.
  const handleLogout = () => {
    setSignedOutIndicator();
    supabase.auth.signOut().finally(() => { window.location.reload(); });
  };

  const NAV_LINKS = [
    { href: "#how-it-works", label: "How It Works" },
    { href: "#portals",      label: "Portals" },
    { href: "#features",     label: "Features" },
    { href: "#voucher-types",label: "Voucher Types" },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF7] text-slate-800">

      {/* ── Navbar ───────────────────────────────────────────────────── */}
      <nav className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-xl border-b border-slate-100/80 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 sm:h-20 flex items-center justify-between">
          <a href="https://valuegiftvouchers.com" className="flex items-center">
            <img src={fullLogo} alt="Value Gift Vouchers" className="h-[264px] object-contain" data-testid="main-logo" />
          </a>
          <div className="hidden md:flex items-center gap-6 lg:gap-8">
            {NAV_LINKS.map(({ href, label }) => (
              <a key={href} href={href}
                className="text-sm font-medium text-slate-500 hover:text-brand-forest transition-colors">
                {label}
              </a>
            ))}
          </div>
          <div className="hidden md:flex items-center gap-3">
            {isLoggedIn ? (
              <>
                <a href={accountHref}>
                  <Button size="sm" className="bg-brand-forest hover:bg-brand-forest/90 text-white shadow-sm">Account</Button>
                </a>
                <Button variant="ghost" size="sm" onClick={handleLogout}
                  className="text-slate-700 hover:text-brand-forest hover:bg-emerald-50 font-medium">
                  Logout
                </Button>
              </>
            ) : (
              <>
                <a href="https://valuegiftvouchers.com/login">
                  <Button variant="ghost" size="sm"
                    className="text-slate-700 hover:text-brand-forest hover:bg-emerald-50 font-medium">
                    Log In
                  </Button>
                </a>
                <a href="https://valuegiftvouchers.com/register">
                  <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white shadow-sm" data-testid="hero-get-started">Get Started</Button>
                </a>
              </>
            )}
          </div>
          <button
            className="md:hidden inline-flex items-center justify-center h-9 w-9 rounded-lg border border-slate-200 text-slate-500"
            onClick={() => setMobileOpen(p => !p)} aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-x-0 top-16 sm:top-20 z-40 bg-white/95 backdrop-blur-lg border-b border-slate-100 shadow-lg">
          <div className="flex flex-col px-4 py-4 gap-1">
            {NAV_LINKS.map(({ href, label }) => (
              <a key={href} href={href} onClick={() => setMobileOpen(false)}
                className="text-sm font-medium text-slate-600 hover:text-brand-forest py-2.5 border-b border-slate-50 last:border-0 transition-colors">
                {label}
              </a>
            ))}
            <div className="pt-3 flex flex-col gap-2">
              {isLoggedIn ? (
                <>
                  <a href={accountHref} onClick={() => setMobileOpen(false)}>
                    <Button size="sm" className="w-full bg-brand-forest text-white">Account</Button>
                  </a>
                  <Button variant="ghost" size="sm"
                    className="w-full text-slate-700 hover:text-brand-forest hover:bg-emerald-50"
                    onClick={() => { handleLogout(); setMobileOpen(false); }}>
                    Logout
                  </Button>
                </>
              ) : (
                <>
                  <a href="https://valuegiftvouchers.com/login" onClick={() => setMobileOpen(false)}>
                    <Button variant="ghost" size="sm" className="w-full text-slate-700 hover:text-brand-forest hover:bg-emerald-50">Log In</Button>
                  </a>
                  <a href="https://valuegiftvouchers.com/register" onClick={() => setMobileOpen(false)}>
                    <Button size="sm" className="w-full bg-orange-500 hover:bg-orange-600 text-white">Get Started</Button>
                  </a>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section className="pt-16 sm:pt-20 bg-[#FAFAF7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 pb-12 md:pt-16 md:pb-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-12 items-center">

          {/* Left */}
          <motion.div className="md:col-span-1 lg:col-span-7" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 mb-5 sm:mb-7 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
              🇬🇭 Ghana's Digital Voucher Platform
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-[68px] font-black leading-tight tracking-tight text-slate-900 mb-4 sm:mb-5">
              Gift.{" "}
              <span className="text-emerald-700">Reward.</span>
              <br />Redeem.
            </h1>
            <p className="text-slate-500 text-base sm:text-lg leading-relaxed mb-6 sm:mb-8 max-w-md">
              Ghana's most flexible digital voucher platform — send gifts, reward employees, and pay merchants via mobile money in seconds.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mb-6 sm:mb-8">
              <a href="#portals" className="w-full sm:w-auto">
                <Button size="lg" className="w-full sm:w-auto bg-emerald-800 hover:bg-emerald-900 text-white px-8 h-12 font-semibold" data-testid="hero-get-started-free">
                  Get Started Free <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </a>
              <a href="#how-it-works" className="w-full sm:w-auto">
                <Button size="lg" variant="outline" className="w-full sm:w-auto border-slate-200 text-slate-600 hover:bg-slate-50 h-12 px-8">
                  See How It Works
                </Button>
              </a>
            </div>
            <p className="flex items-center gap-2 text-slate-400 text-xs mb-3">
              <span className="inline-block w-8 h-px bg-slate-200" />
              Trusted by businesses, NGOs &amp; retailers across Ghana
              <span className="inline-block w-8 h-px bg-slate-200" />
            </p>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {["8 Voucher Types", "2,000+ Merchants", "4 Payment Rails", "Instant Settlement"].map(t => (
                <span key={t} className="flex items-center gap-1.5 text-slate-400 text-xs">
                  <CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />{t}
                </span>
              ))}
            </div>
          </motion.div>

          {/* Right — amber voucher card */}
          <motion.div
            className="md:col-span-1 lg:col-span-5 w-full max-w-[300px] sm:max-w-sm md:max-w-full mx-auto"
            initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
          >
            <div className="w-full rounded-[18px] overflow-hidden shadow-2xl shadow-amber-900/30"
              style={{
                background: "linear-gradient(140deg, #431407 0%, #7c2d12 22%, #b45309 52%, #d97706 76%, #f59e0b 100%)",
                aspectRatio: "1.586",
              }}>
              <div className="relative h-full">
                <div className="absolute inset-0 pointer-events-none"
                  style={{ background: "radial-gradient(ellipse at 68% 28%, rgba(251,191,36,0.30) 0%, transparent 60%)" }} />
                <div className="absolute inset-0 pointer-events-none"
                  style={{ background: "radial-gradient(ellipse at 50% 50%, transparent 38%, rgba(0,0,0,0.38) 100%)" }} />
                <div className="relative h-full flex flex-col justify-between p-4 sm:p-5 md:p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.2em] text-amber-200">Gift Voucher</p>
                      <p className="text-[9px] sm:text-[10px] text-amber-300/50 mt-0.5">All merchants</p>
                    </div>
                    <div className="w-14 h-14 sm:w-18 sm:h-18 md:w-24 md:h-24 bg-white rounded-lg p-1 sm:p-1.5 shadow-lg flex-shrink-0" style={{ width: "clamp(56px, 20%, 96px)", height: "clamp(56px, 20%, 96px)" }}>
                      <QrCode className="w-full h-full text-gray-900" />
                    </div>
                  </div>
                  <div>
                    <p className="text-[8px] font-semibold uppercase tracking-widest text-amber-300/60 mb-0.5 sm:mb-1">Value</p>
                    <p className="font-black text-white leading-none tracking-tight" style={{ fontSize: "clamp(22px, 6vw, 38px)" }}>GH¢ 100.00</p>
                  </div>
                  <div>
                    <p className="font-mono font-bold text-white/80 tracking-[0.15em] mb-2 sm:mb-3" style={{ fontSize: "clamp(10px, 2.8vw, 17px)" }}>ABCD-1234-EFGH-5678</p>
                    <div className="flex items-center justify-between border-t border-white/10 pt-2 sm:pt-3">
                      <p className="text-[8px] sm:text-[9px] text-amber-200/50">Expires 28 Jul 2026</p>
                      <p className="text-[8px] sm:text-[9px] font-bold text-amber-200/65 tracking-wider">Value Gift Vouchers</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Payment rails trust bar ───────────────────────────────────── */}
      <section className="py-8 sm:py-12 bg-white border-t border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <p className="text-center text-[10px] sm:text-[11px] font-semibold tracking-widest uppercase text-stone-400 mb-6 sm:mb-8">
            Trusted Payment &amp; Settlement Rails
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 md:gap-4">
            {[
              { src: "/logos/mtn-momo.svg",     alt: "MTN Mobile Money",  w: "w-24 sm:w-28 md:w-32" },
              { src: "/logos/telecel-cash.svg",  alt: "Telecel Cash",      w: "w-28 sm:w-32 md:w-36" },
              { src: "/logos/airtel-tigo.svg",   alt: "AirtelTigo Money",  w: "w-28 sm:w-32 md:w-40" },
              { src: "/logos/visa.svg",           alt: "VISA",              w: "w-16 sm:w-20 md:w-24" },
              { src: "/logos/mastercard.svg",     alt: "Mastercard",        w: "w-16 sm:w-20 md:w-24" },
              { src: "/logos/ghipss.svg",         alt: "GhIPSS",            w: "w-20 sm:w-24 md:w-28" },
              { src: "/logos/bank-transfer.svg",  alt: "Bank Transfer",     w: "w-20 sm:w-24 md:w-28" },
            ].map(({ src, alt, w }) => (
              <div key={alt} className={`${w} h-12 sm:h-14 md:h-16 flex items-center justify-center p-2 sm:p-3 rounded-xl border border-stone-200 bg-[#FAFAF7] hover:border-emerald-200 transition-colors`}>
                <img src={src} alt={alt} className="w-full h-full object-contain" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ─────────────────────────────────────────────── */}
      <section id="how-it-works" className="py-14 md:py-20 bg-[#FAFAF7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 md:mb-14">
            <p className="text-xs font-semibold tracking-widest uppercase text-emerald-700 mb-3">Simple Process</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-stone-900 mb-3">How It Works</h2>
            <p className="text-stone-500 text-base sm:text-lg">Three simple steps — from issuance to redemption</p>
          </div>
          <div className="relative grid sm:grid-cols-3 gap-5 sm:gap-6 md:gap-8 max-w-4xl mx-auto">
            <div className="hidden sm:block absolute top-10 left-[calc(16.66%+2.5rem)] right-[calc(16.66%+2.5rem)] h-px bg-emerald-100 z-0" />
            {STEPS.map((s, i) => (
              <motion.div key={s.step} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={reveal}
                className="relative z-10 bg-[#FAFAF7] rounded-2xl border border-teal-400 shadow-sm p-6 sm:p-7 md:p-8 text-center">
                <div className={`inline-flex items-center justify-center w-11 h-11 sm:w-12 sm:h-12 rounded-full ${i === 1 ? "bg-amber-500" : "bg-emerald-800"} text-white text-base sm:text-lg font-black mb-4 sm:mb-5 ring-4 ring-white shadow-md`}>
                  {s.step}
                </div>
                <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl ${i === 1 ? "bg-amber-50" : "bg-emerald-50"} flex items-center justify-center mx-auto mb-3 sm:mb-4`}>
                  <s.icon className={`w-4 h-4 sm:w-5 sm:h-5 ${i === 1 ? "text-amber-600" : "text-emerald-700"}`} />
                </div>
                <h3 className="text-base sm:text-lg font-bold text-stone-900 mb-2">{s.title}</h3>
                <p className="text-stone-500 text-xs sm:text-sm leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Portals ───────────────────────────────────────────────────── */}
      <section id="portals" className="py-14 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 md:mb-14">
            <p className="text-xs font-semibold tracking-widest uppercase text-emerald-700 mb-3">The VGV Ecosystem</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-stone-900 mb-3">One Platform, Every Role</h2>
            <p className="text-stone-500 text-base sm:text-lg max-w-2xl mx-auto">
              Dedicated portals built for every participant in the voucher lifecycle.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 max-w-6xl mx-auto">
            {PORTALS.map((p) => (
              <motion.div key={p.key} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={reveal}
                className={`group bg-green-50 rounded-2xl border-l-4 ${p.accent} border border-stone-200 p-5 sm:p-6 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 flex flex-col`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                    <p.icon className="w-5 h-5 text-emerald-700" />
                  </div>
                  <p className="text-xs font-semibold text-stone-400 uppercase tracking-widest">{p.subtitle}</p>
                </div>
                <h3 className="text-sm font-bold text-stone-900 mb-2">{p.title}</h3>
                <p className="text-xs text-stone-500 leading-relaxed mb-3">{p.desc}</p>
                <ul className="space-y-1.5 mb-4 flex-1">
                  {p.features.map(feat => (
                    <li key={feat} className="flex items-center gap-2 text-xs text-stone-500">
                      <span className={`w-1.5 h-1.5 rounded-full ${p.dot} flex-shrink-0`} />
                      {feat}
                    </li>
                  ))}
                </ul>
                <a href={p.registerHref}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-800 group-hover:gap-2.5 transition-all mt-auto">
                  {p.cta} <ChevronRight className="w-3.5 h-3.5" />
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features ─────────────────────────────────────────────────── */}
      <section id="features" className="py-14 md:py-20 bg-[#FAFAF7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 md:mb-14">
            <p className="text-xs font-semibold tracking-widest uppercase text-emerald-700 mb-3">Platform Capabilities</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-stone-900 mb-3">Built for Ghana's Economy</h2>
            <p className="text-stone-500 text-base sm:text-lg max-w-2xl mx-auto">
              From corporate employee rewards to NGO aid distribution, VGV powers every voucher use case on local payment rails.
            </p>
          </div>
          <div className="max-w-4xl mx-auto divide-y divide-stone-100">
            {FEATURES.map((f) => (
              <motion.div key={f.title} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={reveal}
                className="flex flex-col sm:grid sm:grid-cols-[180px_1fr] md:grid-cols-[200px_1fr] gap-3 sm:gap-4 py-5 sm:py-7 items-start">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center flex-shrink-0">
                    <f.icon className="w-4 h-4 text-emerald-700" />
                  </div>
                  <span className="font-semibold text-sm text-stone-800">{f.title}</span>
                </div>
                <p className="text-stone-500 text-sm leading-relaxed pl-11 sm:pl-0">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Voucher Types ─────────────────────────────────────────────── */}
      <section id="voucher-types" className="py-14 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 md:mb-14">
            <p className="text-xs font-semibold tracking-widest uppercase text-emerald-700 mb-3">Flexibility</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-stone-900 mb-3">8 Voucher Types</h2>
            <p className="text-stone-500 text-base sm:text-lg max-w-xl mx-auto">
              From open-loop gift cards to category-specific fuel vouchers — every use case is covered.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 max-w-4xl mx-auto">
            {VOUCHER_TYPES.map((v, i) => (
              <motion.div key={v.label} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={reveal}
                className="bg-green-50 rounded-xl border border-teal-400 p-4 sm:p-5 text-center hover:border-teal-600 hover:shadow-sm transition-all duration-200">
                <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-teal-50 flex items-center justify-center mx-auto mb-2.5 sm:mb-3">
                  <v.icon className="w-5 h-5 text-teal-600" />
                </div>
                <p className="font-semibold text-xs sm:text-sm text-stone-800 mb-1">{v.label}</p>
                <p className="text-xs text-stone-500 leading-snug">{v.desc}</p>
              </motion.div>
            ))}
          </div>
          <p className="text-center text-stone-400 text-sm mt-6 sm:mt-8">
            Need a custom voucher type?{" "}
            <a href={`mailto:${branding.supportEmail}`} className="text-emerald-700 hover:underline">Talk to us →</a>
          </p>
        </div>
      </section>

      {/* ── Use Cases ────────────────────────────────────────────────── */}
      <section className="py-14 md:py-20 bg-[#FAFAF7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 md:mb-14">
            <p className="text-xs font-semibold tracking-widest uppercase text-emerald-700 mb-3">Real-World Applications</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-stone-900 mb-3">Built for Every Use Case</h2>
            <p className="text-stone-500 text-base sm:text-lg max-w-2xl mx-auto">
              Whether you're a corporation, government body, or growing brand — VGV has a voucher solution for you.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5 sm:gap-6 max-w-5xl mx-auto">
            {[
              { icon: Briefcase, bg: "bg-emerald-800", title: "Employee Benefits",
                desc: "Reward and incentivise your workforce with targeted digital vouchers.",
                items: ["Grocery & meal allowances", "Fuel vouchers for field staff", "End-of-year gift distributions", "Performance reward programmes"] },
              { icon: Heart, bg: "bg-amber-500", title: "Aid & Welfare Programmes",
                desc: "Deliver targeted relief directly to beneficiaries — digital or USSD.",
                items: ["Government social cash transfers", "NGO food & fuel relief", "School feeding supplements", "Healthcare access vouchers"] },
              { icon: TrendingUp, bg: "bg-teal-700", title: "Customer Loyalty & Promos",
                desc: "Grow retention with reward campaigns your customers will love.",
                items: ["Retailer loyalty rewards", "Bank & fintech promotions", "Campaign voucher drops", "Partner co-branded programmes"] },
            ].map((uc) => (
              <motion.div key={uc.title} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={reveal}
                className="rounded-2xl overflow-hidden border border-stone-200 bg-white hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
                <div className={`${uc.bg} p-5 sm:p-7`}>
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-white/15 flex items-center justify-center mb-3 sm:mb-4">
                    <uc.icon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-white mb-1.5">{uc.title}</h3>
                  <p className="text-white/70 text-xs sm:text-sm leading-snug">{uc.desc}</p>
                </div>
                <ul className="p-5 sm:p-6 space-y-2.5 sm:space-y-3 bg-white">
                  {uc.items.map(item => (
                    <li key={item} className="flex items-center gap-2.5 text-xs sm:text-sm text-stone-600">
                      <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-500 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="px-5 sm:px-6 pb-4 sm:pb-5 bg-white">
                  <a href="https://corporate.valuegiftvouchers.com/register"
                    className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-800 hover:gap-2 transition-all">
                    Learn more <ChevronRight className="w-3.5 h-3.5" />
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────────────── */}
      <section className="py-14 md:py-20 bg-emerald-800">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={reveal}>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4">
              Ready to Transform Your Voucher Programme?
            </h2>
            <p className="text-white/60 text-base sm:text-lg mb-7 sm:mb-9 max-w-xl mx-auto">
              Join Ghana's leading digital voucher ecosystem. Issue your first voucher in minutes.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-6 sm:mb-7">
              <a href="https://corporate.valuegiftvouchers.com/register" className="w-full sm:w-auto">
                <Button size="lg" className="w-full sm:w-auto bg-amber-500 hover:bg-amber-400 text-white px-9 h-12 font-semibold shadow-lg shadow-emerald-900/30">
                  Start Issuing Vouchers <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </a>
              <a href="https://merchant.valuegiftvouchers.com/register" className="w-full sm:w-auto">
                <Button size="lg" variant="outline"
                  className="w-full sm:w-auto border-white/30 text-white bg-white/10 hover:bg-white/20 px-9 h-12">
                  Become a Merchant
                </Button>
              </a>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-5">
              {["No setup fees", "Free to start", "Bank of Ghana compliant", "Instant onboarding"].map(t => (
                <span key={t} className="flex items-center gap-1.5 text-white/50 text-xs">
                  <CheckCircle className="w-3 h-3 text-emerald-300 flex-shrink-0" />
                  {t}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Footer ───────────────────────────────────────────────────── */}
      <footer className="bg-stone-950 text-stone-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 md:gap-10 mb-8 sm:mb-10">
            <div className="col-span-2 md:col-span-1">
              <p className="text-sm font-bold text-stone-300 mb-2">Value Gift Vouchers</p>
              <p className="text-sm text-stone-500 leading-relaxed">Ghana's secure, flexible, multi-merchant digital voucher platform.</p>
            </div>
            <div>
              <p className="text-[11px] font-semibold tracking-widest uppercase text-stone-600 mb-3 sm:mb-4">Portals</p>
              <ul className="space-y-2 sm:space-y-2.5 text-sm">
                {[["Corporate","https://corporate.valuegiftvouchers.com/"],["Merchant","https://merchant.valuegiftvouchers.com/"],
                  ["Agent","https://agent.valuegiftvouchers.com/"],["Consumer","https://app.valuegiftvouchers.com/"]].map(([label,href]) => (
                  <li key={label}><a href={href} className="hover:text-white transition-colors">{label}</a></li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-[11px] font-semibold tracking-widest uppercase text-stone-600 mb-3 sm:mb-4">Platform</p>
              <ul className="space-y-2 sm:space-y-2.5 text-sm">
                {[["How It Works","#how-it-works"],["Portals","#portals"],["Features","#features"],
                  ["Voucher Types","#voucher-types"]].map(([label,href]) => (
                  <li key={label}><a href={href} className="hover:text-white transition-colors">{label}</a></li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-[11px] font-semibold tracking-widest uppercase text-stone-600 mb-3 sm:mb-4">Legal & Support</p>
              <ul className="space-y-2 sm:space-y-2.5 text-sm">
                <li><a href="#privacy" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="#terms" className="hover:text-white transition-colors">Terms of Service</a></li>
                <li><a href={`mailto:${branding.supportEmail}`} className="hover:text-white transition-colors">{branding.supportEmail}</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-stone-800 pt-6 sm:pt-7 flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4 text-xs text-stone-600">
            <p>© {new Date().getFullYear()} Value Gift Vouchers Ltd. All rights reserved.</p>
            <p className="flex items-center gap-2">
              <CheckCircle className="w-3.5 h-3.5 text-emerald-700" />
              Bank of Ghana Compliant · Secure · Flexible · Multi-Merchant
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
